import {
  TOO_MANY_COMBINATIONS_TEXT,
  eventTypesOf,
  expandToSingleFilters,
  splitIdsByType,
} from './eventsQueryModel';

/*
 * Статистика по выборке событий (Task 6, «Счёт №3 — график и точность»).
 *
 * Распределения по правилам и метрикам считает backend (count-selected).
 * Здесь — только то, чего он не отдаёт: длительность, точность по проверке и
 * столбики по дням считаются на клиенте по загруженным событиям. Функции
 * чистые. Поиск дублей отсюда убран: им занимается анализ дублей на backend.
 */

const toMs = (iso) => {
  if (!iso) return null;
  const ms = new Date(iso).getTime();
  return Number.isNaN(ms) ? null : ms;
};

/** Длительность события в секундах: started_at → ended_at. */
export const eventDurationSeconds = (event) => {
  const start = toMs(event?.started_at);
  const end = toMs(event?.ended_at);
  if (start == null || end == null || end < start) return null;
  return (end - start) / 1000;
};

const DAY_OPTIONS = { year: 'numeric', month: '2-digit', day: '2-digit' };
const dayFormatters = new Map();
const dayFormatter = (timeZone) => {
  const key = timeZone || 'Europe/Moscow';
  if (!dayFormatters.has(key)) {
    let formatter;
    try {
      formatter = new Intl.DateTimeFormat('en-CA', { ...DAY_OPTIONS, timeZone: key });
    } catch (err) {
      formatter = new Intl.DateTimeFormat('en-CA', { ...DAY_OPTIONS, timeZone: 'Europe/Moscow' });
    }
    dayFormatters.set(key, formatter);
  }
  return dayFormatters.get(key);
};

/** День события «YYYY-MM-DD» в поясе камеры — день на графике тот же, что в списке. */
export const eventDayKey = (event) => {
  const ms = toMs(event?.confirmed_at || event?.started_at);
  if (ms == null) return null;
  return dayFormatter(event.display_timezone).format(new Date(ms));
};

const dayLabel = (key) => `${key.slice(8, 10)}.${key.slice(5, 7)}`;

/**
 * Точность по ручной проверке: подтверждённые / (подтверждённые + ошибки).
 * «Не уверен» и непроверенные в знаменатель не входят — они ничего не говорят
 * о качестве детекции. Нет ни одного вердикта — точности нет (null), а не 0%.
 */
const reviewBreakdown = (events) => {
  const counts = { confirmed: 0, false_positive: 0, uncertain: 0, unreviewed: 0 };
  events.forEach((event) => {
    const verdict = event.review?.verdict;
    if (verdict && Object.prototype.hasOwnProperty.call(counts, verdict)) counts[verdict] += 1;
    else counts.unreviewed += 1;
  });
  const decided = counts.confirmed + counts.false_positive;
  return { ...counts, accuracy: decided > 0 ? counts.confirmed / decided : null };
};

export const computeEventsStats = (events) => {
  const list = events || [];
  const durations = list.map(eventDurationSeconds).filter((value) => value != null);
  const durationTotal = durations.reduce((sum, value) => sum + value, 0);

  const days = new Map();
  const rules = new Map();
  list.forEach((event) => {
    const day = eventDayKey(event);
    if (day) days.set(day, (days.get(day) || 0) + 1);
    const ruleKey = event.rule_id || '';
    const rule = rules.get(ruleKey) || { id: ruleKey, name: event.rule_name || event.rule_id || '—', count: 0 };
    rule.count += 1;
    rules.set(ruleKey, rule);
  });

  return {
    total: list.length,
    excluded: list.filter((event) => event.excluded_from_analytics).length,
    durationTotal,
    durationAvg: durations.length ? durationTotal / durations.length : null,
    review: reviewBreakdown(list),
    byDay: [...days.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([day, count]) => ({ day, label: dayLabel(day), count })),
    byRule: [...rules.values()].sort((a, b) => b.count - a.count),
  };
};

export const formatPercent = (share) => (share == null ? '—' : `${(share * 100).toFixed(1).replace('.', ',')}%`);

const toNumberOrNull = (value) => {
  if (value === '' || value === null || value === undefined) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const ID_FILTER_KEYS = ['camera_id', 'factory_id', 'workshop_id', 'section_id', 'room_id'];
const STRING_FILTER_KEYS = ['rule_id', 'status', 'duplicate_state', 'from', 'to'];

const toBooleanOrNull = (value) => {
  if (value === true || value === 'true') return true;
  if (value === false || value === 'false') return false;
  return null;
};

/**
 * Тело POST /violations/events/count-selected строго по контракту: либо
 * `{event_ids}` (режим ids), либо `{filters}` (режим filters), никогда оба.
 * Фильтр — снимок параметров списка: id приводятся к числам, analytics_excluded
 * к boolean, from/to уходят строкой как есть, пустые поля и limit/offset
 * отбрасываются. Нет выбранных id — null (запрос не нужен).
 */
export const buildCountSelectedPayload = ({ mode, ids, filters }) => {
  if (mode === 'filters') {
    const source = filters || {};
    const clean = {};
    ID_FILTER_KEYS.forEach((key) => {
      const value = toNumberOrNull(source[key]);
      if (value !== null) clean[key] = value;
    });
    STRING_FILTER_KEYS.forEach((key) => {
      const value = source[key];
      if (typeof value === 'string' && value !== '') clean[key] = value;
    });
    const excluded = toBooleanOrNull(source.analytics_excluded);
    if (excluded !== null) clean.analytics_excluded = excluded;
    return { filters: clean };
  }
  const list = [...new Set([...(ids || [])].map(Number).filter(Number.isFinite))];
  return list.length > 0 ? { event_ids: list } : null;
};

/**
 * Короткая подпись выбора: по ней видно, что сохранённый результат «Посчитать»
 * относится к другому набору событий и устарел.
 */
export const selectionSignature = ({ mode, ids, filters, count }) => {
  if (mode === 'filters') return `filters:${count}:${JSON.stringify(filters || {})}`;
  const sorted = [...(ids || [])].map(Number).sort((a, b) => a - b);
  let hash = 5381;
  sorted.forEach((id) => { hash = ((hash * 33) ^ id) >>> 0; });
  return `ids:${sorted.length}:${hash}`;
};

/**
 * Запросы «Посчитать» по категориям событий. count-selected у нарушений и у
 * бизнес-метрик свой и принимает одиночные значения фильтра, поэтому выбор
 * делится: id — по типу отмеченного события, фильтр — на сочетания одиночных
 * значений для каждого выбранного типа. Ответы складывает mergeCountResults.
 * Возвращает { available, reason, requests: [{ category, body }] }.
 */
export const planCountRequests = ({ mode, ids, types, filters }) => {
  if (mode === 'filters') {
    const singles = expandToSingleFilters(filters || {});
    if (!singles) return { available: false, reason: TOO_MANY_COMBINATIONS_TEXT, requests: [] };
    const requests = eventTypesOf(filters).flatMap((category) => singles.map((single) => ({
      category,
      body: buildCountSelectedPayload({ mode: 'filters', filters: single }),
    })));
    return { available: true, reason: '', requests };
  }
  const byType = splitIdsByType(ids, types);
  const requests = ['violation', 'business']
    .filter((category) => byType[category].length > 0)
    .map((category) => ({ category, body: { event_ids: [...new Set(byType[category])] } }));
  return { available: requests.length > 0, reason: '', requests };
};
